import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface Category {
  id: string;
  code: string;
  name: string;
  display_order: number;
}

interface Subcategory {
  id: string;
  category_id: string;
  code: string;
  name: string;
  display_order: number;
}

interface CategoryAccordionProps {
  categories: Category[];
  subcategories?: Subcategory[];
  selectedCategory?: string;
  selectedSubcategory?: string;
  onCategorySelect: (categoryCode: string | undefined) => void;
  onSubcategorySelect: (subcategoryCode: string | undefined) => void;
  categoryCounts?: Record<string, number>;
  isLoading?: boolean;
}

export default function CategoryAccordion({
  categories,
  subcategories = [],
  selectedCategory,
  selectedSubcategory,
  onCategorySelect,
  onSubcategorySelect,
  categoryCounts = {},
  isLoading = false,
}: CategoryAccordionProps) {
  const { t } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    selectedCategory ? new Set([selectedCategory]) : new Set()
  );

  const toggleCategory = (categoryCode: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryCode)) {
      newExpanded.delete(categoryCode);
    } else {
      newExpanded.add(categoryCode);
    }
    setExpandedCategories(newExpanded);
  };

  const handleCategoryClick = (categoryCode: string) => {
    if (selectedCategory === categoryCode) {
      // Deselect if clicking the same category
      onCategorySelect(undefined);
      onSubcategorySelect(undefined);
    } else {
      // Select category
      onCategorySelect(categoryCode);
      toggleCategory(categoryCode);
    }
  };

  const handleSubcategoryClick = (subcategoryCode: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedSubcategory === subcategoryCode) {
      // Deselect if clicking the same subcategory
      onSubcategorySelect(undefined);
    } else {
      // Select subcategory
      onSubcategorySelect(subcategoryCode);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="text-sm font-medium mb-2">{t("manualsExtra.categoriesLabel")}</div>
      {categories.length === 0 ? (
        <div className="text-sm text-muted-foreground p-2">{t("manualsExtra.noCategory")}</div>
      ) : (
        <div className="space-y-0.5">
          {categories.map((category) => {
            const isExpanded = expandedCategories.has(category.code);
            const isSelected = selectedCategory === category.code;
            const categorySubcategories = subcategories.filter(
              sub => sub.category_id === categories.find(c => c.code === category.code)?.id
            );
            const count = categoryCounts[category.code] || 0;

            return (
              <div key={category.id}>
                <div
                  className={cn(
                    'flex items-center gap-2 p-1.5 rounded-md cursor-pointer hover:bg-muted transition-colors',
                    isSelected && 'bg-primary/10 font-medium text-primary'
                  )}
                  onClick={() => handleCategoryClick(category.code)}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategory(category.code);
                    }}
                    className="p-0.5 hover:bg-muted rounded"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  <span className="text-sm flex-1">{category.name}</span>
                  {count > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {count}
                    </span>
                  )}
                </div>

                {/* Subcategories - same style as categories */}
                {isExpanded && categorySubcategories.length > 0 && (
                  <div className="space-y-0.5 mt-0.5">
                    {categorySubcategories.map((subcategory) => {
                      const isSubSelected = selectedSubcategory === subcategory.code;
                      return (
                        <div
                          key={subcategory.id}
                          className={cn(
                            'flex items-center gap-2 p-1.5 rounded-md cursor-pointer hover:bg-muted transition-colors',
                            isSubSelected && 'bg-primary/10 font-medium text-primary'
                          )}
                          onClick={(e) => handleSubcategoryClick(subcategory.code, e)}
                        >
                          <span className="text-sm flex-1">{subcategory.name}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

